import React, { useState, useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { AnimatePresence } from "./components/AnimatePresenceWrapper";
import { Event } from "./components/EventCard";
import { Login } from "./components/Login";
import { Toaster } from "./components/ui/sonner";
import { UserProvider } from "./context/UserContext";
import HomePage from "./pages/Home";
import AboutPage from "./pages/About";
import NotFound from "./pages/NotFound";
import SingleEvent from "./pages/SingleEvent";
import OrganizationHomepage from "./pages/OrganizationHomepage";
import Receipt from "./pages/Receipt";
import MyTickets from "./pages/MyTickets";
import PaymentHistory from "./pages/PaymentHistory";
import PricingPage from "./pages/Pricing";
import EventsPage from "./pages/Events";
import CorePage from "./pages/Modules/Core";
import StudioPage from "./pages/Modules/Studio";
import TicketsPage from "./pages/Modules/Tickets";
import AnalyticsPage from "./pages/Modules/Analytics";
import WorkspacePage from "./pages/Modules/Workspace";
import CommunityPage from "./pages/Modules/Community";
import ApiPage from "./pages/Modules/Api";
import {
  isCustomDomain,
  isMainDomain,
  getOrganizationSlugFromSubdomain,
} from "./lib/utils/domain";

// Dashboard imports
import LoginDashboard from "./pages/Auth/Login";
import RegisterDashboard from "./pages/Auth/Register";
import Dashboard from "./pages/_Protected/Dashboard";
import { DashboardProvider } from "./context/DashboardContext";
import { OrganizationsProvider } from "./context/OrganizationsContext";
import Overview from "./pages/_Protected/Overview";
import EventsPageDashboard from "./pages/_Protected/Events";
import NewEventPage from "./pages/_Protected/Events/New";
import SingleEventDashboard from "./pages/_Protected/Events/SingleEvent";
import OrganizationsPage from "./pages/_Protected/Organization";
import NewOrganizationPage from "./pages/_Protected/Organization/NewOrganization";
import SingleOrganization from "./pages/_Protected/Organization/SingleOrganization";
import CraftPageBuilder from "./pages/_Protected/Organization/CraftPageBuilder";
import GrapesPageBuilder from "./pages/_Protected/Organization/GrapesPageBuilder";
import ProfilePage from "./pages/_Protected/Profile";
import ThemePreview from "./pages/_Protected/Organization/ThemePreview";

interface TicketTier {
  id: string;
  name: string;
  price: number;
  description: string;
  remaining: number;
  total: number;
  features: string[];
  color: string;
  popular?: boolean;
}

type EventWithTickets = Event & {
  ticketTiers: TicketTier[];
  fullDescription?: string;
  included?: string[];
};

// Component to handle smooth scroll to top on specific routes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top smoothly when navigating to /, /home, or /about
    if (pathname === "/" || pathname === "/home" || pathname === "/about") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [pathname]);

  return null;
}

// Root layout component that includes ScrollToTop
function RootLayout() {
  return (
    <div className="min-h-screen bg-white">
      <ScrollToTop />
      <Outlet />
    </div>
  );
}

// Component wrapper for attendee routes with login modal functionality
function AttendeeRoutesWrapper({ children }: { children: React.ReactNode }) {
  const [showLogin, setShowLogin] = useState(false);
  const [loginOrganizationSlug, setLoginOrganizationSlug] = useState<
    string | undefined
  >();

  // Mock organization data
  const organizationData = {
    name: "Tech Innovation Hub",
    tagline: "Empowering the Future of Technology & Innovation",
    description:
      "Tech Innovation Hub is a leading organization dedicated to fostering innovation, collaboration, and learning in the technology sector. We bring together industry leaders, innovators, and enthusiasts to create transformative experiences through our carefully curated events, workshops, and conferences. Join us as we shape the future of technology together.",
    location: "San Francisco, CA",
    website: "https://techinnovationhub.com",
    email: "hello@techinnovationhub.com",
    phone: "+1 (555) 123-4567",
    coverImage:
      "https://images.unsplash.com/photo-1581093805071-a04e696db334?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW18ZW58MXx8fHwxNzY0MTY0ODk5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    logoImage:
      "https://images.unsplash.com/photo-1581093805071-a04e696db334?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW18ZW58MXx8fHwxNzY0MTY0ODk5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  };

  return (
    <>
      {children}
      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            organizationName={organizationData.name}
            organizationId={1}
            organizationSlug={loginOrganizationSlug}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// Component to conditionally render attendee routes based on domain
// This is used as a catch-all route in createBrowserRouter
function ConditionalAttendeeRoutes() {
  const onMainDomain = isMainDomain();
  const onCustomDomain = isCustomDomain();

  if (onMainDomain) {
    return (
      <AttendeeRoutesWrapper>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/modules/core" element={<CorePage />} />
          <Route path="/modules/studio" element={<StudioPage />} />
          <Route path="/modules/tickets" element={<TicketsPage />} />
          <Route path="/modules/analytics" element={<AnalyticsPage />} />
          <Route path="/modules/workspace" element={<WorkspacePage />} />
          <Route path="/modules/community" element={<CommunityPage />} />
          <Route path="/modules/api" element={<ApiPage />} />
          <Route
            path="/organization/:organizationSlug"
            element={<OrganizationHomepage />}
          />
          <Route
            path="/organization/:organizationSlug/events"
            element={<EventsPage />}
          />
          <Route
            path="/event/:organizationSlug/:eventID"
            element={<SingleEvent onLoginClick={() => {}} />}
          />
          <Route path="/receipt" element={<Receipt />} />
          <Route path="/:organizationSlug/my-tickets" element={<MyTickets />} />
          <Route
            path="/:organizationSlug/payment-history"
            element={<PaymentHistory />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AttendeeRoutesWrapper>
    );
  } else if (onCustomDomain) {
    return (
      <AttendeeRoutesWrapper>
        <Routes>
          <Route path="/" element={<OrganizationHomepage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route
            path="/event/:eventID"
            element={<SingleEvent onLoginClick={() => {}} />}
          />
          <Route path="/my-tickets" element={<MyTickets />} />
          <Route path="/payment-history" element={<PaymentHistory />} />
          <Route path="/receipt" element={<Receipt />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AttendeeRoutesWrapper>
    );
  } else {
    return (
      <AttendeeRoutesWrapper>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/modules/core" element={<CorePage />} />
          <Route path="/modules/studio" element={<StudioPage />} />
          <Route path="/modules/tickets" element={<TicketsPage />} />
          <Route path="/modules/analytics" element={<AnalyticsPage />} />
          <Route path="/modules/workspace" element={<WorkspacePage />} />
          <Route path="/modules/community" element={<CommunityPage />} />
          <Route path="/modules/api" element={<ApiPage />} />
          <Route
            path="/organization/:organizationSlug"
            element={<OrganizationHomepage />}
          />
          <Route
            path="/organization/:organizationSlug/events"
            element={<EventsPage />}
          />
          <Route
            path="/event/:organizationSlug/:eventID"
            element={<SingleEvent onLoginClick={() => {}} />}
          />
          <Route path="/receipt" element={<Receipt />} />
          <Route path="/:organizationSlug/my-tickets" element={<MyTickets />} />
          <Route
            path="/:organizationSlug/payment-history"
            element={<PaymentHistory />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AttendeeRoutesWrapper>
    );
  }
}

function App() {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: <RootLayout />,
          children: [
            {
              index: true,
              element: <ConditionalAttendeeRoutes />,
            },
            {
              path: "/login",
              element: <LoginDashboard />,
            },
            {
              path: "/register",
              element: <RegisterDashboard />,
            },
            {
              path: "/d",
              element: (
                <DashboardProvider>
                  <OrganizationsProvider>
                    <Dashboard />
                  </OrganizationsProvider>
                </DashboardProvider>
              ),
              children: [
                {
                  index: true,
                  element: <EventsPageDashboard />,
                },
                {
                  path: "/d/overview",
                  element: <Overview />,
                },
                {
                  path: "/d/events",
                  element: <EventsPageDashboard />,
                },
                {
                  path: "/d/events/new",
                  element: <NewEventPage />,
                },
                {
                  path: "/d/events/:id/edit",
                  element: <NewEventPage />,
                },
                {
                  path: "/d/events/:id",
                  element: <SingleEventDashboard />,
                },
                {
                  path: "/d/organizations",
                  element: <OrganizationsPage />,
                },
                {
                  path: "/d/organization/new",
                  element: <NewOrganizationPage />,
                },
                {
                  path: "/d/organizations/:id",
                  element: <SingleOrganization />,
                },
                {
                  path: "/d/organizations/:id/craft-page-builder",
                  element: <CraftPageBuilder />,
                },
                {
                  path: "/d/organizations/:id/grapes-page-builder",
                  element: <GrapesPageBuilder />,
                },
                // Commented out Puck routes - using Craft.js now
                // {
                //   path: "/d/organizations/:id/page-builder/:pageType",
                //   element: <PageBuilder />,
                // },
                // {
                //   path: "/d/events/:id/page-builder/:pageType",
                //   element: <PageBuilder />,
                // },
                {
                  path: "/d/profile",
                  element: <ProfilePage />,
                },
              ],
            },
            {
              path: "/preview/organizations/:id/:themeId",
              element: <ThemePreview />,
            },
            {
              path: "*",
              element: <ConditionalAttendeeRoutes />,
            },
          ],
        },
      ]),
    []
  );

  return (
    <UserProvider>
      <Toaster />
      <RouterProvider router={router} />
    </UserProvider>
  );
}

export default App;
